import { useMemo } from 'react';
import { InlineEditor } from './InlineEditor';
import { SelectInput, type SelectInputOption } from './SelectInput';
import { TextInput } from './TextInput';

export interface RegionalSettingsInlineEditorValue {
  operationalTimeZone: string;
  languageCode: string;
  timePattern: string;
  datePattern: string;
}

export interface RegionalSettingsInlineEditorProps {
  /** Floating label on the summary line. Defaults to `Regional Settings`. */
  label?: string;
  value: RegionalSettingsInlineEditorValue;
  onChange: (next: RegionalSettingsInlineEditorValue) => void;
  timeZoneOptions?: SelectInputOption[];
  languageOptions?: SelectInputOption[];
  timePatternOptions?: SelectInputOption[];
  datePatternOptions?: SelectInputOption[];
  /** Controlled open state. */
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

/**
 * Regional settings editor — lifted from My.Tms.Old/components/RegionalSettingsEditor.tsx.
 * Each select is optional; when an options list is omitted the field falls
 * back to a plain text input so apps can wire data progressively.
 */
export function RegionalSettingsInlineEditor({
  label = 'Regional Settings',
  value,
  onChange,
  timeZoneOptions,
  languageOptions,
  timePatternOptions,
  datePatternOptions,
  isOpen,
  onOpenChange,
  className,
}: RegionalSettingsInlineEditorProps) {
  const summary = useMemo(
    () =>
      `${value.operationalTimeZone} - ${value.languageCode} - ${value.timePattern} - ${value.datePattern}`,
    [value.operationalTimeZone, value.languageCode, value.timePattern, value.datePattern],
  );

  const setField = <K extends keyof RegionalSettingsInlineEditorValue>(
    key: K,
    next: RegionalSettingsInlineEditorValue[K],
  ) => {
    onChange({ ...value, [key]: next });
  };

  return (
    <InlineEditor
      label={label}
      summary={summary}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      ariaSubject={label}
      className={className}
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {timeZoneOptions ? (
          <SelectInput
            label="Operational Time Zone"
            name="operationalTimeZone"
            value={value.operationalTimeZone}
            onChange={(e) => setField('operationalTimeZone', e.target.value)}
            options={timeZoneOptions}
          />
        ) : (
          <TextInput
            label="Operational Time Zone"
            name="operationalTimeZone"
            value={value.operationalTimeZone}
            onChange={(e) => setField('operationalTimeZone', e.target.value)}
          />
        )}
        {languageOptions ? (
          <SelectInput
            label="Language"
            name="languageCode"
            value={value.languageCode}
            onChange={(e) => setField('languageCode', e.target.value)}
            options={languageOptions}
          />
        ) : (
          <TextInput
            label="Language"
            name="languageCode"
            value={value.languageCode}
            onChange={(e) => setField('languageCode', e.target.value)}
          />
        )}
        {timePatternOptions ? (
          <SelectInput
            label="Time Pattern"
            name="timePattern"
            value={value.timePattern}
            onChange={(e) => setField('timePattern', e.target.value)}
            options={timePatternOptions}
          />
        ) : (
          <TextInput
            label="Time Pattern"
            name="timePattern"
            value={value.timePattern}
            onChange={(e) => setField('timePattern', e.target.value)}
          />
        )}
        {datePatternOptions ? (
          <SelectInput
            label="Date Pattern"
            name="datePattern"
            value={value.datePattern}
            onChange={(e) => setField('datePattern', e.target.value)}
            options={datePatternOptions}
          />
        ) : (
          <TextInput
            label="Date Pattern"
            name="datePattern"
            value={value.datePattern}
            onChange={(e) => setField('datePattern', e.target.value)}
          />
        )}
      </div>
    </InlineEditor>
  );
}
